import moment from "moment";
import React, { useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { TableColumn } from "react-data-table-component";
import Moment from "react-moment";
import MultiProgress from "react-multi-progress";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AlertModal from "../../../../../components/alertModal/alertModal";
import CustomRadio from "../../../../../components/customRadio/CustomRadio";
import DataTableBase from "../../../../../components/dataTableBase/DataTableBase";
import EmptyPlaceholder from "../../../../../components/emptyPlaceholder/EmptyPlaceholder";
import {
  IDailyUsage,
  IDailyUsageList,
} from "../../../../../interfaces/IDailyUsage";
import { EPackageType } from "../../../../../interfaces/packageType";
import {
  IProfileData,
  PROFILE_ACTIONS,
} from "../../../../../interfaces/profileData";
import { ISuccessState } from "../../../../../interfaces/successState";
import {
  getDailyUsage,
  getProfileData,
} from "../../../../../services/dashboard";
import { setSelectedDailyUsageMonth } from "../../../../../store/actions/usageSummaryPage.action";
import { IRootState } from "../../../../../store/configureStore";
import { getDataWithCheckingActionPrivilege } from "../../../../../util/hasActionPrivileges";

interface IProps {
  selectedCircuitId?: string;
  selectedPackageType: EPackageType;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSuccess: React.Dispatch<React.SetStateAction<ISuccessState | undefined>>;
}

const BroadbandUsage = (props: IProps) => {
  const { selectedPackageType, selectedCircuitId, setLoading, setIsSuccess } =
    props;

  const { monthIndex } = useSelector(({ usageSummaryPage }: IRootState) => ({
    monthIndex: usageSummaryPage.selectedDailyUsageMonth,
  }));
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const [usageData, setUsageData] = useState<IDailyUsage>();
  const [maxUsage, setMaxUsage] = useState<number>(0);
  const [
    showEnableProtocolReportConfirmation,
    setShowEnableProtocolReportConfirmation,
  ] = useState<boolean>(false);

  useEffect(() => {
    setUsageData(undefined);
    if (selectedCircuitId) {
      setLoading(true);
      getDataWithCheckingActionPrivilege(
        selectedPackageType === EPackageType.BROADBAND,
        selectedCircuitId,
        PROFILE_ACTIONS.USAGE,
        () => getDailyUsage(selectedCircuitId, monthIndex)
      )
        .then((data: IDailyUsage) => {
          if (data.dailyList.length <= 0) {
            throw new Error("No daily usage data for selected month.");
          }
          data = {
            ...data,
            dailyList: data.dailyList.filter((item) => item.totalUsage),
          };
          setUsageData(data);
          setMaxUsage(
            +[...data.dailyList].sort(
              (a: IDailyUsageList, b: IDailyUsageList) =>
                +b.totalUsage - +a.totalUsage
            )[0].totalUsage
          );
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          setIsSuccess({
            isSuccess: false,
            message: error.message,
          });
        });
    }
  }, [
    selectedCircuitId,
    selectedPackageType,
    setLoading,
    monthIndex,
    setIsSuccess,
  ]);

  const showMoreDetailsHandler = (row: IDailyUsageList) => {
    if (!selectedCircuitId) {
      return;
    }
    setLoading(true);
    getProfileData(selectedCircuitId)
      .then((value: IProfileData) => {
        if (value.priviledges.protocol_Report === "false") {
          throw new Error("You don't have permission to view protocol report");
        }

        setLoading(false);
        // if (value.priviledges.purchase_Protocol_Report === 'false') {
        //   setShowEnableProtocolReportConfirmation(true);
        //   return;
        // }

        navigate(`/serviceLineUp/protocolReport/${selectedCircuitId}/${row.date}`);
      })
      .catch((err) => {
        setIsSuccess({
          isSuccess: false,
          message: err.message,
        });
        setLoading(false);
      });
  };

  const protocolReportEnableHandler = () => {
    setShowEnableProtocolReportConfirmation(false);
    navigate("/serviceLineUp/detailedReport", { state: { selectedCircuitId } });
  };

  const getProgressBar = (standardUsage: number, freeUsage: number) => {
    return (
      <MultiProgress
        height={8}
        elements={[
          {
            value: (standardUsage / maxUsage) * 100,
            color: "#26CDE4",
          },
          {
            value: (freeUsage / maxUsage) * 100,
            color: "#E09A28",
          },
          {
            value: ((maxUsage - (standardUsage + freeUsage)) / maxUsage) * 100,
            color: "#818285",
          },
        ]}
      />
    );
  };

  const columns: TableColumn<IDailyUsageList>[] = [
    {
      name: "Date",
      width: "80px",
      cell: (row: IDailyUsageList) => (
        <Moment format="MMM-DD">{row.date}</Moment>
      ),
    },
    {
      name: <div>Standard Usage</div>,
      width: "90px",
      cell: (row: IDailyUsageList) => (
        <span style={{ color: "#26CDE4" }}>
          {row.standardUsage} {row.volumeUnit}
        </span>
      ),
    },
    {
      name: <div>Free Usage</div>,
      width: "80px",
      cell: (row: IDailyUsageList) => (
        <span style={{ color: "#E09A28" }}>
          {row.freeUsage} {row.volumeUnit}
        </span>
      ),
    },
    {
      name: <div>Total Usage</div>,
      width: "80px",
      cell: (row: IDailyUsageList) => `${row.totalUsage} ${row.volumeUnit}`,
    },
    {
      name: "Usage",
      grow: 1,
      cell: (row: IDailyUsageList) =>
        getProgressBar(+row.standardUsage, +row.freeUsage),
    },
    {
      name: "Details",
      width: "80px",
      cell: (row: IDailyUsageList) => (
        <>
          <Button size="sm" onClick={() => showMoreDetailsHandler(row)}>
            More
          </Button>
        </>
      ),
    },
  ];

  return (
    <Row>
      {selectedCircuitId && (
        <Button
          variant="primary"
          style={{
            backgroundColor: "#04559F",
            color: "#fff",
            fontSize: "1rem",
            fontWeight: "bold",
            padding: "20px",
            borderRadius: "16px",
            height: "30px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "20px",
            border: "none",
            marginBottom: "10px",
          }}
          onClick={() =>
            navigate("/serviceLineUp/detailedReport", {
              state: { selectedCircuitId },
            })
          }
        >
          Enable/Disable Protocol Report
        </Button>
      )}
      <Col>
        {selectedCircuitId && (
          <Row>
            <Col>
              <CustomRadio
                selectedIndex={monthIndex}
                onChange={(index: number) => {
                  dispatch(setSelectedDailyUsageMonth(index));
                }}
                items={[
                  {
                    label: "Current Month",
                    index: 0,
                  },
                  {
                    label: (
                      <Moment format="MMM YYYY">
                        {moment().subtract(1, "month")}
                      </Moment>
                    ),
                    index: 1,
                  },
                  {
                    label: (
                      <Moment format="MMM YYYY">
                        {moment().subtract(2, "month")}
                      </Moment>
                    ),
                    index: 2,
                  },
                ]}
              />
            </Col>
          </Row>
        )}
        {usageData?.dailyList && (
          <>
            <Row>
              <Col>
                <DataTableBase
                  className="mt-3"
                  columns={columns}
                  data={usageData?.dailyList}
                />
              </Col>
            </Row>
          </>
        )}

        {(!selectedCircuitId ||
          !usageData ||
          usageData?.dailyList?.length === 0) && (
          <EmptyPlaceholder className="mt-5" />
        )}
      </Col>

      {/* protocol report enable confirmation confirmation */}
      <AlertModal
        show={showEnableProtocolReportConfirmation}
        buttons={[
          {
            variant: "success",
            text: "Yes",
            onClick: protocolReportEnableHandler,
          },
          {
            variant: "danger",
            text: "No",
            onClick: () => {
              setShowEnableProtocolReportConfirmation(false);
            },
          },
        ]}
        variant="error"
      >
        Do you want enable protocol report?
      </AlertModal>
    </Row>
  );
};

export default BroadbandUsage;
